import React, { useEffect, useState, useRef } from "react";
import { notification, Progress, Spin } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import { TrainingAPI } from "../../api/TrainingAPI";
import { REAUTH_ERRORS } from "../../api/tokenProvider";
import { playErrorSound, playSuccessSound } from "./Notifications/soundUtils";

const notificationStyle = {
  width: 500,
}

// The token request fails with a TypeError when the device has no network, for example
// just after a laptop wakes from sleep. Safari says "Load failed", Chrome "Failed to fetch",
// Firefox "NetworkError ...", and auth0-spa-js "Timeout when executing 'fetch'".
const CONNECTIVITY_ERRORS = ["Load failed", "Failed to fetch", "NetworkError", "Timeout when executing 'fetch'"];
const TOKEN_RETRY_DELAYS_MS = [2000, 5000, 10000, 20000];

const isConnectivityError = (err: unknown) =>
  err instanceof Error && CONNECTIVITY_ERRORS.some((m) => err.message.includes(m));

// A dead session is not a fault of this component. The upload request from the same
// Submit goes through the axios interceptor in <Auth>, which sends the user to log in.
const isReauthError = (err: unknown) =>
  REAUTH_ERRORS.has((err as { error?: string } | null)?.error ?? "");

interface IParsedMessages {
  event_type: string;
  message: {
    type: string;
    content: string;
  }
  metadata: {
    persist_message: boolean,
    uuid: string
  }
  progress: number
  status: "done" | 'in-progress' | 'error' | 'success' | 'none',
  timestamp: string
}

export const RealTimeMessageNotification = ({ title = "Loading...", uuid }: { title: string, uuid: string }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [messages, setMessages] = useState<string[]>([]);
  const [api, contextHolder] = notification.useNotification();
  const eventSourceRef = useRef<EventSource | null>(null);
  const trainingAPI = new TrainingAPI();

  useEffect(() => {
    setMessages([]);
  }, [uuid]);

  useEffect(() => {
    if (!uuid) return;

    let isMounted = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    async function setupEventSource(attempt = 0) {
      try {
        const token = await getAccessTokenSilently();

        if (!isMounted) return;

        const eventSource = new EventSource(`${trainingAPI.url}/rte/${uuid}/?token=${token}`);
        eventSourceRef.current = eventSource;

        eventSource.onmessage = (event) => {
          const data = event?.data;
          const parsedData = JSON.parse(data);

          setMessages(prev => [...prev, data]);

          if (parsedData?.status == "done") {
            eventSource.close();
          }
        };

        eventSource.onerror = (err) => {
          eventSource.close();
        };
      } catch (err) {
        if (!isMounted) return;
        if (isReauthError(err)) {
          console.warn("Upload progress: the session expired, so the stream did not open:", err);
          return;
        }
        if (!isConnectivityError(err)) {
          console.error("Failed to setup EventSource:", err);
          return;
        }
        if (attempt < TOKEN_RETRY_DELAYS_MS.length) {
          retryTimer = setTimeout(() => setupEventSource(attempt + 1), TOKEN_RETRY_DELAYS_MS[attempt]);
        }
        console.warn("Upload progress: the token request failed with no network:", err);
      }
    }

    setupEventSource();

    return () => {
      isMounted = false;
      clearTimeout(retryTimer);
      if (eventSourceRef?.current) {
        eventSourceRef?.current?.close();
      }
    };
  }, [uuid, getAccessTokenSilently]);

  const messageStyling: {
    [key in IParsedMessages['status']]: {
      icon: React.ReactElement<any>,
      fontColor: string
    }
  } = {
    'none': {
      icon: <Spin size="small" />,
      fontColor: '#000000'
    },
    'in-progress': {
      icon: <Spin size="small" />,
      fontColor: '#000000'
    },
    'done': {
      icon: <Spin size="small" />,
      fontColor: '#000000'
    },
    'success': {
      icon: <span>&#9989;</span>,
      fontColor: '#008c1c'
    },
    'error': {
      icon: <span>&#10060;</span>,
      fontColor: '#FF0000'
    }
  }

  const determineProgressStatus = (progressCompletion: number, errorPresent: boolean) => {
    if (progressCompletion < 100){ return 'active' }
    if (errorPresent) { return 'exception' }
    else { return 'success' }
  }

  const getFormattedMessages = (messages: IParsedMessages[]) => {
    let formattedMessages: React.ReactElement<any>[] = [];

    let progress = 0;
    const errorPresent = messages.some(e => e.status === 'error')

    messages?.forEach((msg, idx) => {
      const isLatest = idx === messages.length - 1;

      if (!(isLatest || msg?.metadata?.persist_message)) {
        return;
      }
      if (isLatest && msg?.status == "done") {
        if (errorPresent){
          playErrorSound()
        } else {
          playSuccessSound()
        }
        return;
      }

      if (msg?.event_type === "string_and_progress" || msg?.event_type === "progress_update") {
        progress = msg?.progress
      }

      if (msg?.message?.type == "text") {
        formattedMessages.push(
          <div
            key={idx}
            style={{
              color: messageStyling[msg.status].fontColor
            }}
          >
            {messageStyling[msg.status].icon} {msg?.message?.content}
          </div>
        );
      }
    })

    return (
      <>
        {progress > 0 ? <Progress
          percent={progress}
          status={determineProgressStatus(progress, errorPresent)}
          showInfo={progress < 100 ? false : true}
        /> : null}
        {formattedMessages}
      </>
    )
  }

  useEffect(() => {
    if (!uuid) return;
    if (messages?.length === 0) return;

    const parsedMessages: IParsedMessages[] = messages.map(e => JSON.parse(e));

    api.open({
      key: uuid,
      message: title,
      description: getFormattedMessages(parsedMessages),
      placement: "bottomRight",
      duration: 0,
      style: notificationStyle,
      getContainer: () => document.body,
    });
  }, [messages, uuid, title, api]);

  return <>{contextHolder}</>;
};
